import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';

import { DealProvider } from '@/services/ws-deals/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Filter } from '@/js/libs/ui/src/V2';

import { useTranslateOptionLabel } from 'src/hooks/useTranslateOptionLabel';
import type { Option } from 'src/types';

interface DealProviderFilterProps {
  value: Option<DealProvider>;
  onChange: (value: Option<DealProvider>) => void;
  label?: string;
}

export const DealProviderFilter = memo(function DealProviderFilter({ value, onChange, label }: DealProviderFilterProps) {
  const [t] = useTranslation(['backend', 'common']);
  const translateOptionLabel = useTranslateOptionLabel(t);

  const options = useMemo<Option<DealProvider>[]>(
    () =>
      Object.values(DealProvider).map((item) => ({
        label: item,
        value: item,
      })),
    [],
  );

  const handleChange = useCallbackRef((option: SingleValue<Option<DealProvider>>) => {
    if (option) {
      onChange(option);
    }
  });

  return (
    <Filter<DealProvider>
      options={options}
      label={label ?? t('common:PROVIDER')}
      selected={value}
      onChange={handleChange}
      formatOptionLabel={translateOptionLabel}
      isClearable={false}
      dataTestId='provider-filter'
    />
  );
});
